import React from 'react';

import { graphql, Link, PageProps } from 'gatsby';

import { Button } from '@mui/material';

import {
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import { BreadCrumbData, SeoFooter, ServiceTeaser } from '@mewa/ui';

import t from '../../libs/translate';
import { useConversionButtonLabel } from '../a-b-testing/cta-label-experiment';
import { ContactTeaserWrapper } from '../components/contact-teaser/contact-teaser-wrapper';
import { NavigationData } from '../components/header/types';
import { GlobalPageContext } from '../types';
import CollectionPage, {
  Collection,
} from './components/collection-page/collection-page';
import { storeServiceSegment } from './conversion/persistence';

export { Head } from '../layouts/head';

type Props = NavigationData & {
  // This is not really a collection, but we interpret the service segment data
  // as a collection for all none-clothing service segments.
  pimServiceSegment: Omit<Collection, 'products'> & {
    key: ServiceSegmentType;
    productGroups: {
      products: Collection['products'];
    }[];
    seoFooter: {
      childMarkdownRemark: { html: string };
    };
  };
  serviceTestTeaser?: ServiceTeaserData<ServiceTeaserType.cleaningClothsServiceTest>;
  translations: {
    general_more_information: string;
    general_download_brochure: string;
    show_product_details_label: string;
    seo_footer_show_more_label: string;
    seo_footer_show_less_label: string;
    navigation_product_menu_label: string;
  };
};

type PageContext = GlobalPageContext & {
  id: string;
};

export const query = graphql`
  query ($id: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    pimServiceSegment(id: { eq: $id }) {
      name
      slug
      claim
      property
      infoText1
      infoText2
      galleryTitle
      galleryIntroText
      key
      productGroups {
        products {
          ...ProductData
        }
      }
      seoFooter {
        childMarkdownRemark {
          html
        }
      }
      assets {
        keyVisualDesktop
        keyVisualMobile
      }
    }

    serviceTestTeaser: strapiServiceTeaser(
      ServiceType: { eq: "putztuch-service-test" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }

    translations: translation(locale: { eq: $locale }) {
      general_more_information
      general_download_brochure
      show_product_details_label
      seo_footer_show_more_label
      seo_footer_show_less_label
      navigation_product_menu_label
    }
  }
`;

const NonClothingServiceSegment = ({
  data: {
    pimServiceSegment: collection,
    serviceTestTeaser,
    translations,
    navigationLinks,
    contactTeaserLabels,
    headerAndFooterTranslations,
  },
  pageContext: { langPrefix, locale },
}: PageProps<Props, PageContext>) => {
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const collectionData = {
    ...collection,
    products: collection.productGroups[0].products,
  };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    {
      name: navigationLinks.navigation.nonClothing.name,
      slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
    },
    {
      name: collectionData.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.nonClothing.name,
    slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
  };

  return (
    <>
      <CollectionPage
        data-pagefind-meta={`service_segment_type:${collection.key}`}
        isClothingCategory={false}
        langPrefix={langPrefix}
        collection={collectionData}
        breadCrumbData={breadCrumbData}
        mobileBreadCrumbData={mobileBreadCrumbData}
        locale={locale}
        brochuresDownload={{
          text: translations.general_download_brochure,
          url: navigationLinks.conversion.brochuresDownload.slug,
          preSelection: [collection.key],
        }}
        detailsPageLabel={t(translations, 'show_product_details_label')}
        conversion={{
          label: ctaLabel,
          link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
          onClick: () => {
            storeServiceSegment(collection.key);
          },
        }}
        serviceTestTeaser={
          serviceTestTeaser && (
            <ServiceTeaser
              data-pagefind-ignore
              description={serviceTestTeaser.description}
              title={serviceTestTeaser.title}
              type={ServiceTeaserType.cleaningClothsServiceTest}
              link={
                <Button
                  variant="text"
                  color="secondary"
                  component={Link}
                  to={`${langPrefix}/${navigationLinks.conversion.cleaningClothsServiceTestPage.slug}`}
                >
                  {t(translations, 'general_more_information')}
                </Button>
              }
            />
          )
        }
      />

      <ContactTeaserWrapper
        contactTeaserLabels={contactTeaserLabels}
        langPrefix={langPrefix}
        navigationLinks={navigationLinks}
        serviceSegment={collection.key}
      />

      <SeoFooter
        html={collection.seoFooter.childMarkdownRemark.html}
        showMoreLabel={t(translations, 'seo_footer_show_more_label')}
        showLessLabel={t(translations, 'seo_footer_show_less_label')}
      />
    </>
  );
};

NonClothingServiceSegment.noContactTeaser = true;

export default NonClothingServiceSegment;
